import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import { Separator } from "@/core/components/ui/separator";
import { Textarea } from "@/core/components/ui/textarea";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import { apiErrorHandler } from "@/core/api/api-error-handler";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useEarlyUnlockSavingsVault } from "@/features/savings/api/use-early-unlock-savings-vault";
import type { SavingsVaultDto } from "@/features/savings/api/dto";
import {
  createEarlyUnlockExecuteSchema,
  createEarlyUnlockRequestSchema,
  type EarlyUnlockExecuteFormValues,
  type EarlyUnlockRequestFormValues,
} from "@/features/savings/schemas/savings-vault.schema";
import { useSubmitApprovalRequest } from "@/features/shared/api";

/**
 * Déblocage anticipé d'un coffre — flux maker-checker (guide v3.1 §6.10) :
 *
 * 1. **Maker** : ouvre une demande `SAVINGS_EARLY_UNLOCK`
 *    (`ApprovalRequests/add`, sourceService `savings-service`,
 *    resourceType `SAVINGS_VAULT`, resourceId = coffre, payload `"{}"`)
 *    puis renvoie vers la file `/app/admin/approvals` pour approbation par
 *    un compte distinct (règle des quatre yeux).
 * 2. **Checker** : une fois la demande APPROVED, saisit son identifiant et
 *    exécute `POST /SavingsVaults/{id}/early-unlock` (idempotent) — le
 *    savings-service revérifie l'approbation en direct auprès
 *    d'admin-service.
 *
 * Réservé à `flashpay-admin` / `flashpay-superadmin` (gating d'affichage à
 * l'appelant — `VaultActions`). Opération sans frais :
 * `MutationConfirmPreview` sur les deux étapes.
 */
export function VaultEarlyUnlockDialog({
  vault,
  open,
  onOpenChange,
}: {
  vault: SavingsVaultDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTypedTranslation();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const tSavings = (key: string) => t(key as TFunctionType);
  const requestSchema = useMemo(
    () => createEarlyUnlockRequestSchema(),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : schéma zod traduit reconstruit au changement de langue.
    [i18n.language]
  );
  const executeSchema = useMemo(
    () => createEarlyUnlockExecuteSchema(),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : schéma zod traduit reconstruit au changement de langue.
    [i18n.language]
  );

  const submitApproval = useSubmitApprovalRequest();
  const earlyUnlock = useEarlyUnlockSavingsVault(vault.id);
  const [requestPreview, setRequestPreview] =
    useState<EarlyUnlockRequestFormValues | null>(null);
  const [executePreview, setExecutePreview] =
    useState<EarlyUnlockExecuteFormValues | null>(null);

  const requestForm = useForm({
    defaultValues: { reason: "" } as EarlyUnlockRequestFormValues,
    onSubmit: async ({ value }) => {
      const parsed = requestSchema.safeParse(value);
      if (!parsed.success) return;
      setRequestPreview(parsed.data);
    },
  });

  const executeForm = useForm({
    defaultValues: { approvalRequestId: "" } as EarlyUnlockExecuteFormValues,
    onSubmit: async ({ value }) => {
      const parsed = executeSchema.safeParse(value);
      if (!parsed.success) return;
      setExecutePreview(parsed.data);
    },
  });

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{t("savings.vaults.earlyUnlock.title")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t("savings.vaults.earlyUnlock.description")}
          </p>

          {/* Étape 1 — demande d'approbation (maker). */}
          <form
            className="grid grid-cols-1 gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void requestForm.handleSubmit();
            }}
          >
            <requestForm.Field
              name="reason"
              validators={{
                onChange: ({ value }) => {
                  const result = requestSchema.shape.reason.safeParse(value);
                  return result.success
                    ? undefined
                    : result.error.issues[0]?.message;
                },
              }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="savings-early-unlock-reason">
                    {t("savings.vaults.earlyUnlock.reason")}
                  </Label>
                  <Textarea
                    id="savings-early-unlock-reason"
                    placeholder={t(
                      "savings.vaults.earlyUnlock.reasonPlaceholder"
                    )}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </requestForm.Field>
            <div>
              <requestForm.Subscribe selector={(s) => s.isSubmitting}>
                {(isSubmitting) => (
                  <Button
                    type="submit"
                    variant="secondary"
                    disabled={isSubmitting || submitApproval.isPending}
                  >
                    {isSubmitting || submitApproval.isPending ? (
                      <ButtonSpinner />
                    ) : null}
                    {t("savings.vaults.earlyUnlock.submitRequest")}
                  </Button>
                )}
              </requestForm.Subscribe>
            </div>
          </form>

          <Separator />

          {/* Étape 2 — exécution (checker), demande APPROVED. */}
          <form
            className="grid grid-cols-1 gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void executeForm.handleSubmit();
            }}
          >
            <executeForm.Field
              name="approvalRequestId"
              validators={{
                onChange: ({ value }) => {
                  const result =
                    executeSchema.shape.approvalRequestId.safeParse(value);
                  return result.success
                    ? undefined
                    : result.error.issues[0]?.message;
                },
              }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="savings-early-unlock-approval">
                    {t("savings.vaults.earlyUnlock.approvalRequestId")}
                  </Label>
                  <Input
                    id="savings-early-unlock-approval"
                    className="notranslate font-mono"
                    placeholder={t(
                      "savings.vaults.earlyUnlock.approvalRequestIdPlaceholder"
                    )}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t("savings.vaults.earlyUnlock.executeHint")}
                  </p>
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </executeForm.Field>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={earlyUnlock.isPending}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={earlyUnlock.isPending}>
                {earlyUnlock.isPending ? <ButtonSpinner /> : null}
                {t("savings.vaults.earlyUnlock.execute")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview étape 1 : ouverture de la demande maker-checker. */}
      <MutationConfirmPreview
        open={requestPreview !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setRequestPreview(null);
        }}
        data={
          requestPreview
            ? {
                actionType: "SAVINGS_EARLY_UNLOCK",
                sourceService: "savings-service",
                resourceType: "SAVINGS_VAULT",
                resourceId: vault.id,
                vaultName: vault.name,
                reason: requestPreview.reason,
              }
            : {}
        }
        title={t("savings.vaults.earlyUnlock.requestPreviewTitle")}
        description={t("common.mutationPreview.createDescription")}
        confirmLabel={t("savings.vaults.earlyUnlock.submitRequest")}
        t={tSavings}
        pending={submitApproval.isPending}
        onConfirm={() => {
          if (!requestPreview) return;
          submitApproval.mutate(
            {
              actionType: "SAVINGS_EARLY_UNLOCK",
              sourceService: "savings-service",
              resourceType: "SAVINGS_VAULT",
              resourceId: vault.id,
              payload: {},
              reason: requestPreview.reason,
            },
            {
              onSuccess: () => {
                notifSilence(t("savings.vaults.feedback.approvalRequested"));
                setRequestPreview(null);
                onOpenChange(false);
                navigate("/app/admin/approvals");
              },
              onError: (error) =>
                notifSilenceError(apiErrorHandler(error) || error.message),
            }
          );
        }}
      />

      {/* Preview étape 2 : exécution du déblocage. */}
      <MutationConfirmPreview
        open={executePreview !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setExecutePreview(null);
        }}
        data={
          executePreview
            ? {
                vaultId: vault.id,
                vaultName: vault.name,
                approvalRequestId: executePreview.approvalRequestId,
              }
            : {}
        }
        title={t("savings.vaults.earlyUnlock.executePreviewTitle")}
        description={t("common.mutationPreview.description")}
        confirmLabel={t("savings.vaults.earlyUnlock.execute")}
        t={tSavings}
        pending={earlyUnlock.isPending}
        onConfirm={() => {
          if (!executePreview) return;
          earlyUnlock.mutate(
            { approvalRequestId: executePreview.approvalRequestId },
            {
              onSuccess: () => {
                notifSilence(t("savings.vaults.feedback.earlyUnlocked"));
                setExecutePreview(null);
                onOpenChange(false);
              },
              onError: (error) =>
                notifSilenceError(apiErrorHandler(error) || error.message),
            }
          );
        }}
      />
    </>
  );
}
